
import React, { useState, useMemo } from 'react';
import {
    Box,
    Heading,
    Text,
    SimpleGrid,
    Card,
    CardHeader,
    CardBody,
    Icon,
    Flex,
    Badge,
    Container,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Image,
    useDisclosure,
    VStack,
    HStack,
    Tag,
    TagLabel,
    TagCloseButton
} from '@chakra-ui/react';
import { eplStyles, commonStyles } from '../clientstyles/commonstyles';
import Navbar from "../NewPages/HomePage/Navbar";
import Footer from "../NewPages/HomePage/Footer";
import { FaSearch, FaArrowRight, FaMagic, FaFilter } from 'react-icons/fa';

const StyleDocs = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStyle, setSelectedStyle] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Define available style collections here. 
    // You can import more styles and add them to this object.
    const styleCollections = useMemo(() => ({
        "EPL": eplStyles,
        "Common": commonStyles,
        // "Nature": natureStyles, 
        // "IEEE": ieeeStyles 
    }), []);

    const [activeFilters, setActiveFilters] = useState(Object.keys(styleCollections));

    // Flatten styles into a single array with source metadata
    const allStyles = useMemo(() => {
        let styles = [];
        Object.entries(styleCollections).forEach(([source, collection]) => {
            Object.entries(collection).forEach(([key, style]) => {
                styles.push({
                    id: key,
                    source: source,
                    ...style
                });
            });
        });
        return styles;
    }, [styleCollections]);

    const filteredStyles = allStyles.filter(style => {
        const matchesSearch =
            (style.name && style.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (style.description && style.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
            style.id.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = activeFilters.includes(style.source);

        return matchesSearch && matchesFilter;
    });

    const toggleFilter = (source) => {
        if (activeFilters.includes(source)) {
            if (activeFilters.length > 1) {
                setActiveFilters(activeFilters.filter(f => f !== source));
            }
        } else {
            setActiveFilters([...activeFilters, source]);
        }
    };

    const handleCardClick = (style) => {
        setSelectedStyle(style);
        onOpen();
    };

    const BrandColor = "#f44f0d";

    return (
        <Box display="flex" flexDirection="column" minHeight="100vh" bg="white">
            <Navbar />
            <Container maxW="container.xl" flex="1" pt={32} pb={20}>
                <VStack spacing={8} mb={16} textAlign="center">
                    <Heading
                        as="h1"
                        size="2xl"
                        color="#15141C"
                        position="relative"
                        _after={{
                            content: '""',
                            width: '60px',
                            height: '4px',
                            bg: BrandColor,
                            position: 'absolute',
                            bottom: '-16px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            borderRadius: 'full'
                        }}
                    >
                        Automated Style Rules
                    </Heading>
                    <Text fontSize="xl" color="gray.600" maxW="2xl" pt={4}>
                        Explore the intelligent formatting rules that ensure your documents meet the highest standards.
                    </Text>

                    <Flex w="full" justify="center" position="relative" alignItems="center">
                        <VStack spacing={4} w="full" maxW="md">
                            <InputGroup size="lg">
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={FaSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search for a rule..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    borderRadius="full"
                                    focusBorderColor={BrandColor}
                                    bg="gray.50"
                                    _hover={{ bg: "gray.100" }}
                                />
                            </InputGroup>

                            {/* Source Filters */}
                            <HStack spacing={2} wrap="wrap" justify="center">
                                <Text fontSize="sm" fontWeight="bold" color="gray.500" mr={2}>
                                    <Icon as={FaFilter} mr={1} /> Sources:
                                </Text>
                                {Object.keys(styleCollections).map(source => (
                                    <Tag
                                        key={source}
                                        size="lg"
                                        borderRadius="full"
                                        variant={activeFilters.includes(source) ? "solid" : "outline"}
                                        colorScheme={activeFilters.includes(source) ? "orange" : "gray"}
                                        cursor="pointer"
                                        onClick={() => toggleFilter(source)}
                                        transition="all 0.2s"
                                    >
                                        <TagLabel>{source}</TagLabel>
                                        {activeFilters.includes(source) && activeFilters.length > 1 && (
                                            <TagCloseButton onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFilter(source);
                                            }} />
                                        )}
                                    </Tag>
                                ))}
                            </HStack>
                        </VStack>

                        <Box position={{ base: 'static', md: 'absolute' }} right={0} bottom={0} mt={{ base: 4, md: 0 }}>
                            <Text fontSize="sm" color="gray.500">
                                Showing <b>{filteredStyles.length}</b> of <b>{allStyles.length}</b> rules
                            </Text>
                        </Box>
                    </Flex>
                </VStack>

                <SimpleGrid columns={[1, null, 2, 3]} spacing={8}>
                    {filteredStyles.map((style) => (
                        <Card
                            key={`${style.source}-${style.id}`}
                            bg="white"
                            borderRadius="2xl"
                            border="1px solid"
                            borderColor="gray.100"
                            boxShadow="sm"
                            cursor="pointer"
                            transition="all 0.3s ease"
                            _hover={{
                                boxShadow: "xl",
                                transform: "translateY(-4px)",
                                borderColor: "orange.100"
                            }}
                            onClick={() => handleCardClick(style)}
                            overflow="hidden"
                        >
                            <Box h="4px" bg={BrandColor} w="0%" transition="width 0.3s" _groupHover={{ w: "100%" }} />
                            <CardHeader pb={2}>
                                <Flex justify="space-between" align="start" mb={3}>
                                    <Badge
                                        colorScheme="blue"
                                        borderRadius="md"
                                        px={2}
                                        py={1}
                                        fontSize="xs"
                                        fontWeight="bold"
                                    >
                                        {style.source}
                                    </Badge>
                                    <Icon as={FaMagic} color={BrandColor} boxSize={5} opacity={0.6} ml={2} />
                                </Flex>
                                <Heading size="md" color="#15141C" fontWeight="bold" lineHeight="tall">
                                    {style.name}
                                </Heading>
                            </CardHeader>
                            <CardBody pt={0}>
                                <Text color="gray.600" fontSize="sm" noOfLines={3} mb={4}>
                                    {style.description}
                                </Text>
                                <Flex align="center" color={BrandColor} fontSize="sm" fontWeight="semibold" mt="auto">
                                    View Details <Icon as={FaArrowRight} ml={2} w={3} h={3} />
                                </Flex>
                            </CardBody>
                        </Card>
                    ))}
                </SimpleGrid>

                {/* Detail Modal */}
                <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered scrollBehavior="inside">
                    <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
                    <ModalContent borderRadius="2xl" overflow="hidden">
                        <ModalHeader bg="gray.50" py={6} borderBottom="1px solid" borderColor="gray.100">
                            <Flex justify="space-between" align="start">
                                <VStack align="start" spacing={1}>
                                    <HStack>
                                        <Badge colorScheme="blue" fontSize="sm">{selectedStyle?.source}</Badge>
                                        <Heading size="lg" color="#15141C">{selectedStyle?.name}</Heading>
                                    </HStack>
                                    <Text fontSize="sm" color="gray.500" fontWeight="normal">Rule ID: {selectedStyle?.id}</Text>
                                </VStack>
                            </Flex>
                        </ModalHeader>
                        <ModalCloseButton mt={4} />

                        <ModalBody p={[6, 8]}>
                            <VStack align="stretch" spacing={8}>
                                <Box>
                                    <Heading size="sm" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={3}>
                                        Description
                                    </Heading>
                                    <Text fontSize="lg" color="#15141C" lineHeight="1.8">
                                        {selectedStyle?.description}
                                    </Text>
                                </Box>

                                {(selectedStyle?.beforeImage || selectedStyle?.afterImage) ? (
                                    <Box>
                                        <Heading size="sm" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={6}>
                                            Transformation Preview
                                        </Heading>
                                        <SimpleGrid columns={[1, 2]} spacing={8}>
                                            {selectedStyle.beforeImage && (
                                                <Box>
                                                    <Text mb={2} fontWeight="bold" color="red.500" display="flex" align="center">
                                                        <Badge colorScheme="red" mr={2}>BEFORE</Badge>
                                                    </Text>
                                                    <Box
                                                        border="1px solid"
                                                        borderColor="gray.200"
                                                        borderRadius="xl"
                                                        overflow="hidden"
                                                        boxShadow="md"
                                                    >
                                                        <Image src={selectedStyle.beforeImage} alt="Before correction" w="100%" objectFit="cover" fallbackSrc="https://via.placeholder.com/400x200?text=No+Preview+Available" />
                                                    </Box>
                                                </Box>
                                            )}
                                            {selectedStyle.afterImage && (
                                                <Box>
                                                    <Text mb={2} fontWeight="bold" color="green.500" display="flex" align="center">
                                                        <Badge colorScheme="green" mr={2}>AFTER</Badge>
                                                    </Text>
                                                    <Box
                                                        border="1px solid"
                                                        borderColor="gray.200"
                                                        borderRadius="xl"
                                                        overflow="hidden"
                                                        boxShadow="md"
                                                    >
                                                        <Image src={selectedStyle.afterImage} alt="After correction" w="100%" objectFit="cover" fallbackSrc="https://via.placeholder.com/400x200?text=No+Preview+Available" />
                                                    </Box>
                                                </Box>
                                            )}
                                        </SimpleGrid>
                                    </Box>
                                ) : (
                                    <Box bg="gray.50" p={6} borderRadius="xl" textAlign="center" color="gray.500">
                                        <Text fontStyle="italic">No visual preview available for this rule.</Text>
                                    </Box>
                                )}
                            </VStack>
                        </ModalBody>

                        <ModalFooter bg="gray.50" py={4}>
                            <Button onClick={onClose} size="lg">Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

            </Container>
            <Box w="100%">
                <Footer />
            </Box>
        </Box>
    );
};

export default StyleDocs;
